import React, {useState, useEffect} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {
  getAccountVendors,
  getAccountVendorCreditById,
  createAccountVendorCredit,
  updateAccountVendorCredit,
  getAccountPurchaseOrders,
} from '../core/_requests'
import SearchableSelect from '../../../shared/components/SearchableSelect'

export default function AccountsNewVendorCredit() {
  const navigate = useNavigate()
  const {id} = useParams()
  const creditId = id ? Number(id) : null
  const isEditing = creditId !== null && !Number.isNaN(creditId)

  const [saving, setSaving] = useState(false)
  const [loadingInitial, setLoadingInitial] = useState(false)

  // Vendor dropdown data
  const [vendors, setVendors] = useState<any[]>([])
  // Purchase orders data
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([])

  // Form state
  const [vendorId, setVendorId] = useState('')
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('')
  const [creditNoteNumber, setCreditNoteNumber] = useState('')
  const [referenceNumber, setReferenceNumber] = useState('')
  const [creditDate, setCreditDate] = useState(new Date().toISOString().split('T')[0])
  const [subTotal, setSubTotal] = useState('')
  const [discountPercent, setDiscountPercent] = useState('')
  const [discountAmount, setDiscountAmount] = useState('')
  const [taxType, setTaxType] = useState('')
  const [taxName, setTaxName] = useState('')
  const [taxAmount, setTaxAmount] = useState('')
  const [adjustment, setAdjustment] = useState('')
  const [balance, setBalance] = useState('')
  const [notes, setNotes] = useState('')
  const [status, setStatus] = useState('Open')

  // Computed total
  const computedSubTotal = Number(subTotal) || 0
  const computedDiscount = Number(discountAmount) || 0
  const computedTax = Number(taxAmount) || 0
  const computedAdjustment = Number(adjustment) || 0
  const computedTotal = computedSubTotal - computedDiscount + computedTax + computedAdjustment

  useEffect(() => {
    getAccountVendors()
      .then((data) => setVendors(data || []))
      .catch((err) => console.error('Failed to load vendors:', err))

    getAccountPurchaseOrders()
      .then((data) => setPurchaseOrders(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Failed to load purchase orders:', err))
  }, [])

  useEffect(() => {
    if (!isEditing || creditId == null) return
    setLoadingInitial(true)
    getAccountVendorCreditById(creditId)
      .then((data: any) => {
        if (data) {
          setVendorId(data.vendorId ? String(data.vendorId) : '')
          setPurchaseOrderNumber(data.purchaseOrderNumber || data.purchase_order_number || '')
          setCreditNoteNumber(data.creditNoteNumber || '')
          setReferenceNumber(data.referenceNumber || '')
          setCreditDate(data.creditDate || new Date().toISOString().split('T')[0])
          setSubTotal(data.subTotal !== undefined ? String(data.subTotal) : '')
          setDiscountPercent(data.discountPercent !== undefined ? String(data.discountPercent) : '')
          setDiscountAmount(data.discountAmount !== undefined ? String(data.discountAmount) : '')
          setTaxType(data.taxType || '')
          setTaxName(data.taxName || '')
          setTaxAmount(data.taxAmount !== undefined ? String(data.taxAmount) : '')
          setAdjustment(data.adjustment !== undefined ? String(data.adjustment) : '')
          setBalance(data.balance !== undefined ? String(data.balance) : '')
          setNotes(data.notes || '')
          setStatus(data.status || 'Open')
        }
      })
      .catch((err) => console.error('Failed to load vendor credit:', err))
      .finally(() => setLoadingInitial(false))
  }, [isEditing, creditId])

  const filteredPurchaseOrders = React.useMemo(() => {
    if (!vendorId) return []
    return purchaseOrders.filter((po: any) => {
      const poVendorId = po.vendorId ?? po.vendor_id
      return String(poVendorId) === String(vendorId)
    })
  }, [purchaseOrders, vendorId])

  const hasCurrentPO = Boolean(
    purchaseOrderNumber &&
      filteredPurchaseOrders.some(
        (po: any) => (po.purchaseOrderNumber || po.purchase_order_number) === purchaseOrderNumber
      )
  )

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!vendorId || !creditNoteNumber || !creditDate) {
      alert('Please fill in Vendor, Credit Note Number, and Credit Date.')
      return
    }

    setSaving(true)
    const selectedVendor = vendors.find((v) => v.id === Number(vendorId))

    const payload = {
      vendorId: Number(vendorId),
      vendorName: selectedVendor ? (selectedVendor.displayName || selectedVendor.companyName || selectedVendor.name) : null,
      purchaseOrderNumber: purchaseOrderNumber || null,
      creditNoteNumber,
      referenceNumber: referenceNumber || null,
      creditDate,
      subTotal: computedSubTotal,
      discountPercent: Number(discountPercent) || 0,
      discountAmount: computedDiscount,
      taxType: taxType || null,
      taxName: taxName || null,
      taxAmount: computedTax,
      adjustment: computedAdjustment,
      total: computedTotal,
      balance: isEditing ? Number(balance) || 0 : computedTotal,
      notes: notes || null,
      status,
    }

    try {
      if (isEditing && creditId) {
        await updateAccountVendorCredit(creditId, payload)
      } else {
        await createAccountVendorCredit(payload)
      }
      navigate('/accounts/purchase/vendor-credits')
    } catch (err: any) {
      console.error('Save failed:', err)
      alert('Failed to save vendor credit')
    } finally {
      setSaving(false)
    }
  }

  const inputClass =
    'w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all'

  if (loadingInitial) {
    return (
      <div className='flex h-screen w-full items-center justify-center bg-slate-50'>
        <p className='text-slate-500 font-medium'>Loading Vendor Credit Details...</p>
      </div>
    )
  }

  return (
    <>
      <div className='flex h-screen w-full overflow-hidden bg-slate-50'>
        <div id='sidebar-overlay' className='fixed inset-0 bg-black/50 z-40 hidden md:hidden'></div>
        <main className='flex-1 flex flex-col h-full overflow-hidden relative'>
          <div className='flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6' id='main-scroll'>
            <div className='max-w-5xl mx-auto pb-20'>
              <div className='mb-6'>
                <div className='text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1'>
                  Accounts / Purchase / Vendor Credits
                </div>
                <h1 className='text-2xl font-bold text-slate-900 leading-tight'>
                  {isEditing ? 'Edit Vendor Credit' : 'New Vendor Credit'}
                </h1>
                <p className='text-sm text-slate-500'>
                  {isEditing ? 'Update vendor credit details' : 'Record a new vendor credit'}
                </p>
              </div>

              <form
                onSubmit={handleSave}
                className='bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6'
              >
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div className='mb-5'>
                    <label className='block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider'>
                      Vendor Name <span className='text-red-500'>*</span>
                    </label>
                    <SearchableSelect
                      value={vendorId}
                      onChange={(e) => setVendorId(e.target.value)}
                      required
                      className={inputClass}
                    >
                      <option value=''>Select a vendor...</option>
                      {vendors.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.displayName}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                  <div className='mb-5'>
                    <label className='block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider'>
                      Purchase Order# (PO#)
                    </label>
                    <SearchableSelect
                      value={purchaseOrderNumber}
                      onChange={(e) => setPurchaseOrderNumber(e.target.value)}
                      className={inputClass}
                      disabled={!vendorId}
                    >
                      <option value=''>
                        {vendorId
                          ? 'Select a Purchase Order (optional)...'
                          : 'Select a vendor first...'}
                      </option>
                      {!hasCurrentPO && purchaseOrderNumber && (
                        <option value={purchaseOrderNumber}>
                          {purchaseOrderNumber} (Selected Reference)
                        </option>
                      )}
                      {filteredPurchaseOrders.map((po: any) => {
                        const poNum = po.purchaseOrderNumber || po.purchase_order_number
                        const poDate = po.orderDate || po.order_date
                        const poTotal = po.total != null ? Number(po.total).toFixed(2) : null
                        return (
                          <option key={po.id} value={poNum}>
                            {poNum} {poDate ? `(${poDate})` : ''} {poTotal ? `- $${poTotal}` : ''}
                          </option>
                        )
                      })}
                    </SearchableSelect>
                  </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div className='mb-5'>
                    <label className='block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider'>
                      Credit Note# <span className='text-red-500'>*</span>
                    </label>
                    <input
                      type='text'
                      value={creditNoteNumber}
                      onChange={(e) => setCreditNoteNumber(e.target.value)}
                      required
                      placeholder='CN-0001'
                      className={inputClass}
                    />
                  </div>
                  <div className='mb-5'>
                    <label className='block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider'>
                      Reference#
                    </label>
                    <input
                      type='text'
                      value={referenceNumber}
                      onChange={(e) => setReferenceNumber(e.target.value)}
                      placeholder='Original Bill# or Ref'
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div className='mb-5'>
                    <label className='block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider'>
                      Credit Date <span className='text-red-500'>*</span>
                    </label>
                    <input
                      type='date'
                      value={creditDate}
                      onChange={(e) => setCreditDate(e.target.value)}
                      required
                      className={inputClass}
                    />
                  </div>
                  {isEditing && (
                    <div className='mb-5'>
                      <label className='block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider'>
                        Status
                      </label>
                      <SearchableSelect
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className={inputClass}
                      >
                        <option value='Open'>Open</option>
                        <option value='Closed'>Closed</option>
                      </SearchableSelect>
                    </div>
                  )}
                </div>

                <hr className='my-6 border-slate-100' />

                {/* Totals */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div className='mb-5'>
                    <label className='block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider'>
                      Sub Total
                    </label>
                    <input
                      type='number'
                      step='0.01'
                      value={subTotal}
                      onChange={(e) => setSubTotal(e.target.value)}
                      placeholder='0.00'
                      className={inputClass}
                    />
                  </div>
                  <div className='mb-5'>
                    <label className='block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider'>
                      Discount Amount
                    </label>
                    <input
                      type='number'
                      step='0.01'
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(e.target.value)}
                      placeholder='0.00'
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div className='mb-5'>
                    <label className='block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider'>
                      Tax Name
                    </label>
                    <input
                      type='text'
                      value={taxName}
                      onChange={(e) => setTaxName(e.target.value)}
                      placeholder='e.g. VAT'
                      className={inputClass}
                    />
                  </div>
                  <div className='mb-5'>
                    <label className='block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider'>
                      Tax Amount
                    </label>
                    <input
                      type='number'
                      step='0.01'
                      value={taxAmount}
                      onChange={(e) => setTaxAmount(e.target.value)}
                      placeholder='0.00'
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div className='mb-5'>
                    <label className='block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider'>
                      Adjustment
                    </label>
                    <input
                      type='number'
                      step='0.01'
                      value={adjustment}
                      onChange={(e) => setAdjustment(e.target.value)}
                      placeholder='0.00'
                      className={inputClass}
                    />
                  </div>
                  {isEditing && (
                    <div className='mb-5'>
                      <label className='block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider'>
                        Balance
                      </label>
                      <input
                        type='number'
                        step='0.01'
                        value={balance}
                        onChange={(e) => setBalance(e.target.value)}
                        placeholder='0.00'
                        className={inputClass}
                      />
                    </div>
                  )}
                </div>

                <div className='flex justify-end mb-6'>
                  <div className='w-72 space-y-2 text-sm'>
                    <div className='flex justify-between'>
                      <span className='text-slate-500'>Sub Total</span>
                      <span className='font-semibold'>{computedSubTotal.toFixed(2)}</span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-slate-500'>Discount</span>
                      <span>-{computedDiscount.toFixed(2)}</span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-slate-500'>Tax</span>
                      <span>{computedTax.toFixed(2)}</span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-slate-500'>Adjustment</span>
                      <span>{computedAdjustment.toFixed(2)}</span>
                    </div>
                    <hr className='border-slate-200' />
                    <div className='flex justify-between text-base font-bold'>
                      <span>Total Credit ($)</span>
                      <span>{computedTotal.toFixed(2)}</span>
                    </div>
                    {isEditing && (
                      <div className='flex justify-between text-base font-bold text-emerald-600'>
                        <span>Remaining Balance ($)</span>
                        <span>{Number(balance).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <hr className='my-6 border-slate-100' />

                <div className='mb-5'>
                  <label className='block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider'>
                    Notes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder='Internal notes about this vendor credit'
                    className={`${inputClass} resize-none`}
                  ></textarea>
                </div>

                <div className='pt-6 mt-6 border-t border-slate-100 flex items-center gap-3'>
                  <button
                    type='submit'
                    disabled={saving}
                    className='px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow transition-all disabled:opacity-50'
                  >
                    {saving ? 'Saving...' : isEditing ? 'Update Credit' : 'Save Credit'}
                  </button>
                  <button
                    type='button'
                    onClick={() => navigate(-1)}
                    className='px-6 py-2.5 text-slate-500 hover:text-slate-700 text-sm font-bold transition-colors'
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </>
  )
}
